// AUTO-GENERATED — DO NOT EDIT THIS FILE DIRECTLY.
//
// Synced from useAuthToken.js
// by scripts/sync_wallet_app.sh on 2026-07-17T08:09:17Z.
//
// To make changes:
//   1. Edit react/_shared/wallet-app/useAuthToken.js
//   2. Run ./scripts/sync_wallet_app.sh
//
// Editing this copy in place will be overwritten on next sync.
//
// ---------------------------------------------------------------

// useAuthToken.js
//
// Wallet-JWT auth module for the Bankcoin customer apps. Extracted from
// react/depositor-web-v2/src/components/auth.js and stripped to the
// single-app shape used by mint / bridge / fx.

import { useEffect, useState } from 'react';
import { buildUrl } from './api/client';

const STORAGE_KEY = 'bankcoin.auth.v1';
const EXPIRY_SKEW_SEC = 60;

let _token = null;
let _role = null;
let _address = null;
let _inflightAuth = null;

const _authListeners = new Set();

function _notify() {
  for (const listener of _authListeners) {
    try {
      listener();
    } catch (_) {
      // Subscriber errors must not break other subscribers.
    }
  }
}

export function subscribeAuth(listener) {
  _authListeners.add(listener);
  return () => _authListeners.delete(listener);
}

// React hook: current JWT (or null), re-renders the caller on auth change.
export function useAuthToken() {
  const [token, setToken] = useState(_token);
  useEffect(() => subscribeAuth(() => setToken(_token)), []);
  return token;
}

// React hook: { token, role, address } convenience.
export function useAuth() {
  const [snap, setSnap] = useState({ token: _token, role: _role, address: _address });
  useEffect(
    () => subscribeAuth(() => setSnap({ token: _token, role: _role, address: _address })),
    [],
  );
  return snap;
}

// ── JWT helpers ──────────────────────────────────────────────────────────────

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  try {
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function isJwtUnexpired(token) {
  const p = decodeJwtPayload(token);
  if (!p) return false;
  const exp = Number(p.exp || 0);
  if (!Number.isFinite(exp) || exp <= 0) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp - now > EXPIRY_SKEW_SEC;
}

// ── localStorage persistence ─────────────────────────────────────────────────

function persistToStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (!_token || !_address) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ token: _token, role: _role, address: _address }),
    );
  } catch (_) {
    // Private mode / quota — fall back to in-memory only.
  }
}

function loadFromStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.token !== 'string') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

(function restoreOnBoot() {
  const restored = loadFromStorage();
  if (!restored) return;
  if (!isJwtUnexpired(restored.token)) {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return;
  }
  _token = restored.token;
  _role = restored.role || null;
  _address = restored.address || null;
})();

// ── Accessors ────────────────────────────────────────────────────────────────

export const getToken = () => _token;
export const getRole = () => _role;
export const getAuthAddress = () => _address;

export function isAuthenticated() {
  if (!_token) return false;
  return isJwtUnexpired(_token);
}

export function isAuthenticatedFor(address) {
  if (!isAuthenticated()) return false;
  if (!address || !_address) return false;
  return _address.toLowerCase() === address.toLowerCase();
}

export function setAuth(token, role, address) {
  _token = token;
  _role = role;
  _address = address;
  persistToStorage();
  _notify();
}

export function clearAuth() {
  const had = _token !== null;
  _token = null;
  _role = null;
  _address = null;
  persistToStorage();
  if (had) _notify();
}

// ── Wallet authentication ────────────────────────────────────────────────────

/**
 * Sign the canonical login message and exchange for a JWT.
 *
 * @param {string} address - Connected wallet
 * @param {(args:{message:string})=>Promise<string>} signMessage - viem WalletClient.signMessage adapter
 * @returns {Promise<{token:string, role?:string}>}
 */
export async function authenticateWallet(address, signMessage) {
  if (!address) throw new Error('wallet address required');

  if (isAuthenticatedFor(address)) {
    return { token: _token, role: _role };
  }

  if (_inflightAuth) return _inflightAuth;

  _inflightAuth = (async () => {
    let message;
    let signature;
    if (typeof signMessage === 'function') {
      const timestamp = Math.floor(Date.now() / 1000);
      message = `Sign in to Bankcoin Capital\nTimestamp: ${timestamp}`;
      signature = await signMessage({ message });
    }

    const res = await fetch(buildUrl('/auth/wallet'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address, message, signature }),
    });

    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `auth failed: ${res.status}`);
    }

    const j = await res.json();
    setAuth(j.token, j.role, address);
    return { token: j.token, role: j.role };
  })();

  try {
    return await _inflightAuth;
  } finally {
    _inflightAuth = null;
  }
}

// On 401 from any authed call, the auth state is invalid — clear it.
export function onUnauthorized() {
  if (_token) clearAuth();
}
